import type { Area } from '~/common/types/area';
import { useTranslation } from 'react-i18next';
import { cn } from '~/common/utils/styleUtil';
import { getLanguageValue, webshopConfig } from '~/config/webshopConfig';
import { getLinkRel } from '~/common/utils/urlUtil';
import { FooterIcon } from '~/shell/components/footer/FooterIcon';
import { FooterSocialLinks } from '~/shell/components/footer/FooterSocialLinks';
import { FooterLegalLinks } from '~/shell/components/footer/FooterLegalLinks';
import { FooterDeploymentInfo } from '~/shell/components/footer/FooterDeploymentInfo';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '~/common/components/ui/accordion';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';

export interface Footer2Props {
  area: Area;
}

export function Footer({ area }: Readonly<Footer2Props>) {
  const { t, i18n } = useTranslation();
  const footerData = webshopConfig.footer;

  return (
    <>
      {/* Desktop Footer */}
      <div
        className={
          'hidden desktop:block 2xl:container 2xl:mx-auto gap-8 desktop:px-8 max-w-screen w-full overflow-hidden'
        }
      >
        <div className={'flex gap-36'}>
          {footerData.segments.map(segment => (
            <div className={'my-8'} key={segment.titleKey}>
              <p className={'text-lg font-bold text-primary mb-2'}>{t(segment.titleKey as any)}</p>
              {segment.descriptionKey && (
                <div className={'flex min-h-10 items-center'}>
                  <p className={'text'}>{t(segment.descriptionKey as any)}</p>
                </div>
              )}
              <ul>
                {segment.links
                  .filter(l => !l.area || l.area === area)
                  .map(({ titleKey, icon, highlighted, urlDe, urlFr, urlIt }) => {
                    const link = getLanguageValue(i18n.language, { de: urlDe, fr: urlFr, it: urlIt });
                    const showBlank = !link.startsWith('tel');
                    const title = t(titleKey as any);
                    return (
                      <li
                        key={titleKey}
                        className={cn('flex items-center py-2 hover:text-primary gap-2', highlighted && 'text-primary')}
                      >
                        {icon && <FooterIcon icon={icon} size={18} />}
                        <TrackNavigationClick label={title}>
                          <a
                            href={link}
                            target={showBlank ? '_blank' : '_self'}
                            rel={getLinkRel(link)}
                            data-testid={`footer-${titleKey}`}
                          >
                            {title}
                          </a>
                        </TrackNavigationClick>
                      </li>
                    );
                  })}
              </ul>
            </div>
          ))}
        </div>
        <div className={'flex justify-between'}>
          <FooterSocialLinks links={footerData.social} />
          <FooterDeploymentInfo />
          <FooterLegalLinks links={footerData.legal} />
        </div>
      </div>

      {/* Mobile Footer */}
      <div className={'desktop:hidden container mx-auto'}>
        <Accordion type="single" collapsible>
          {footerData.segments.map(s => (
            <AccordionItem key={s.titleKey} value={s.titleKey}>
              <AccordionTrigger
                className={'py-8 px-4 font-bold text-primary hover:no-underline'}
                data-testid={`${s.titleKey}`}
              >
                {t(s.titleKey as any)}
              </AccordionTrigger>
              <AccordionContent className={'px-4'}>
                {s.descriptionKey && <p className={'text-base min-h-8'}>{t(s.descriptionKey as any)}</p>}
                <ul key={s.titleKey}>
                  {s.links
                    .filter(l => !l.area || l.area === area)
                    .map(({ titleKey, icon, highlighted, urlDe, urlFr, urlIt }) => {
                      const link = getLanguageValue(i18n.language, { de: urlDe, fr: urlFr, it: urlIt });
                      const showBlank = !link.startsWith('tel');
                      const title = t(titleKey as any);

                      return (
                        <li
                          key={titleKey}
                          className={cn(
                            'items-center py-2 text-base hover:text-primary flex gap-2',
                            highlighted && 'text-primary'
                          )}
                        >
                          {icon && <FooterIcon icon={icon} size={18} />}
                          <TrackNavigationClick label={title}>
                            <a
                              href={link}
                              target={showBlank ? '_blank' : '_self'}
                              rel={getLinkRel(link)}
                              data-testid={`${s.titleKey}`}
                            >
                              {title}
                            </a>
                          </TrackNavigationClick>
                        </li>
                      );
                    })}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className={'flex flex-col items-center mb-4'}>
          <FooterSocialLinks links={footerData.social} />
          <FooterLegalLinks links={footerData.legal} />
          <FooterDeploymentInfo />
        </div>
      </div>
    </>
  );
}
